import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import { X, CheckCircle, PencilSimple, FloppyDisk } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/date";

const STATUS = {
  draft: { t: "Draft", dot: "#8A857A", c: "bg-surface-inset text-ink-secondary border-border" },
  submitted: { t: "Needs approval", dot: "#C99A00", c: "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]" },
  foreman_approved: { t: "Office approval", dot: "#C99A00", c: "bg-[#FBF2CC] text-[#7A5B00] border-[#EAD79A]" },
  office_approved: { t: "Approved", dot: "#2E7D32", c: "bg-[#E4F2E4] text-[#256428] border-[#BFE0C0]" },
  rejected: { t: "Rejected", dot: "#B42318", c: "bg-[#FBE7E4] text-[#B42318] border-[#F1C4BC]" },
};
const NEXT_LABEL = { submitted: "Foreman Approve", foreman_approved: "Office Approve" };

const inp = "w-full h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none";
const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";
const cardLbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted";

function Card({ title, action, children, testid }) {
  return (
    <section className="bg-surface-card border border-border rounded-[12px] overflow-hidden" data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-divider-inner">
          <h4 className="font-bold text-[15px] text-ink-primary">{title}</h4>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

function Field({ k, v, strong }) {
  return (
    <div>
      <div className={cardLbl + " mb-1"}>{k}</div>
      <div className={`text-[15px] leading-tight ${strong ? "font-bold text-ink-primary" : "font-medium text-ink-secondary"}`}>{v}</div>
    </div>
  );
}

function SigCard({ role, sig, onCapture }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(sig?.name || "");
  const [img, setImg] = useState("");
  const title = role === "operator" ? "Worker" : "Site Rep";
  return (
    <div className="bg-surface-card border border-border rounded-[12px] overflow-hidden" data-testid={`docket-sig-${role}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-divider-inner">
        <span className={cardLbl}>{title}</span>
        {!open && <button onClick={() => setOpen(true)} data-testid={`docket-sign-${role}-btn`} className="text-[12px] font-bold text-accent-ink hover:underline">{sig?.image ? "Re-sign" : "Capture signature"}</button>}
      </div>
      {!open ? (
        <div className="px-4 py-3 min-h-[104px]">
          <div className="text-[15px] font-semibold text-ink-primary">{sig?.name || "—"}</div>
          {sig?.image ? <img src={sig.image} alt="signature" className="h-14 mt-1" />
            : sig?.unavailable ? <div className="text-[12px] text-[#8A5B00] mt-1">Unavailable: {sig.reason || "—"}</div>
            : <div className="text-[13px] text-ink-tertiary mt-2">Not signed</div>}
        </div>
      ) : (
        <div className="px-4 py-3 space-y-2">
          <input className={inp} placeholder={`${title} name`} value={name} onChange={(e) => setName(e.target.value)} data-testid={`docket-sign-${role}-name`} />
          <SignaturePad onChange={setImg} testid={`docket-sign-${role}-pad`} />
          <div className="flex gap-2">
            <button onClick={() => { onCapture(role, name, img); setOpen(false); }} data-testid={`docket-sign-${role}-save`}
              className="h-8 px-3 bg-equipr-black text-white text-xs font-bold rounded-[7px] hover:bg-gray-800">Save signature</button>
            <button onClick={() => setOpen(false)} className="h-8 px-3 border border-border-control text-xs rounded-[7px]">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DocketDetailModal({ docket, onClose, onChanged }) {
  const { isWriter } = useAuth();
  const [d, setD] = useState(docket);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(docket);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [projTasks, setProjTasks] = useState([]);
  const [projName, setProjName] = useState(docket.project_name || "");

  useEffect(() => {
    if (docket.project_id) {
      api.get(`/projects/${docket.project_id}`)
        .then(({ data }) => {
          setProjTasks((data.tasks || []).filter((t) => t.active !== false));
          setProjName(data.project_name || data.site_address || "");
        })
        .catch(() => {});
    }
  }, [docket.project_id]);

  const setTaskEntry = (i, patch) => setForm((s) => ({ ...s, task_entries: (s.task_entries || []).map((x, j) => j === i ? { ...x, ...patch } : x) }));

  const st = STATUS[d.status] || STATUS.submitted;
  const nxt = NEXT_LABEL[d.status];
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const reload = async () => {
    const { data } = await api.get(`/dockets/${d.id}`);
    setD(data); setForm(data); onChanged?.();
  };

  const act = async (kind) => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/dockets/${d.id}/${kind}`);
      const newStatus = kind === "reject" ? "rejected" : data?.status;
      if (newStatus) { setD((p) => ({ ...p, status: newStatus })); setForm((p) => ({ ...p, status: newStatus })); }
      await reload();
    }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const capture = async (role, name, image) => {
    setBusy(true); setError("");
    try {
      await api.post(`/dockets/${d.id}/sign`, { role, name, image, unavailable: false, reason: "" });
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const saveAmend = async () => {
    setBusy(true); setError("");
    try {
      await api.put(`/dockets/${d.id}`, {
        ...form,
        down_time_hours: Number(form.down_time_hours) || 0,
        machine_start: form.machine_start === "" ? null : Number(form.machine_start),
        machine_finish: form.machine_finish === "" ? null : Number(form.machine_finish),
        task_entries: (form.task_entries || [])
          .filter((t) => (t.name || "").trim() || t.task_id)
          .map((t) => ({ ...t, hours: t.hours === "" || t.hours == null ? null : Number(t.hours) })),
      });
      setEditing(false);
      await reload();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const smuUsed = d.machine_start != null && d.machine_finish != null ? (d.machine_finish - d.machine_start).toFixed(1) : "—";
  const taskTotal = (d.task_entries || []).reduce((s, t) => s + (Number(t.hours) || 0), 0);
  const worker = d.signatures?.operator?.name || d.worker_name || "the worker";

  // Trail text
  const trailParts = [];
  if ((d.approvals || []).length) {
    d.approvals.forEach((a) => trailParts.push(`${(a.stage || "").replace(/_/g, " ")} by ${a.by}`));
  } else if (d.status !== "draft") {
    trailParts.push(`submitted by ${worker}`);
  }
  if (d.status === "submitted") trailParts.push("awaiting foreman approval");
  else if (d.status === "foreman_approved") trailParts.push("awaiting office approval");
  else if (d.status === "office_approved") trailParts.push("passed to office for invoicing");
  else if (d.status === "rejected") trailParts.push("returned to worker");
  const trailText = trailParts.join(" · ") || "No activity yet.";

  const footerHint = nxt
    ? `Approving passes ${d.worked_hours ?? 0} hrs to the office for invoicing. Rejecting returns the docket to ${worker} with your reason.`
    : "";

  return (
    <div className="fixed inset-0 z-[60] bg-[rgba(26,26,26,0.42)] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-card border border-border-warm rounded-[18px] overflow-hidden shadow-modal w-full max-w-[640px] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="docket-detail-modal">

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-divider-inner shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink-muted">Docket</div>
              <h3 className="font-mono text-[26px] font-bold text-ink-primary leading-tight mt-0.5" data-testid="docket-detail-no">{d.docket_no}</h3>
              <p className="text-[13.5px] text-ink-secondary mt-1 truncate">
                {[d.asset_no, fmtDate(d.date), projName].filter(Boolean).join(" · ")}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.06em] rounded-full border ${st.c}`} data-testid="docket-detail-status">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: st.dot }} /> {st.t}
              </span>
              <button onClick={onClose} data-testid="docket-detail-close" className="w-9 h-9 flex items-center justify-center rounded-[9px] border border-border-control text-ink-secondary hover:bg-surface-panel"><X size={16} weight="bold" /></button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 overflow-auto space-y-4 bg-surface-inset">
          {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-[8px]" data-testid="docket-detail-error">{error}</div>}

          {d.off_roster && (
            <div className="flex items-start gap-2 border border-amber-300 bg-amber-50 text-amber-800 text-[13px] px-3 py-2 rounded-[8px]" data-testid="docket-off-roster">
              <span className="font-bold">⚠ Off-roster docket:</span>
              <span>submitted by <b>{d.submitted_by || "a worker"}</b> who wasn't rostered on this project — please review.</span>
            </div>
          )}

          {/* Working times */}
          <Card title="Working times" testid="docket-working-times"
            action={isWriter && !editing ? <button onClick={() => setEditing(true)} data-testid="docket-edit-btn" className="text-[13px] font-bold text-accent-ink hover:underline inline-flex items-center gap-1"><PencilSimple size={14} /> Amend</button> : null}>
            {!editing ? (
              <>
                <div className="grid grid-cols-3 gap-x-6 gap-y-4 p-4">
                  <Field k="Shift" v={d.shift || "—"} />
                  <Field k="Start" v={d.start_time || "—"} />
                  <Field k="Finish" v={d.finish_time || "—"} />
                  <Field k="Worked hrs" v={d.worked_hours ?? "—"} strong />
                  <Field k="Down time" v={`${d.down_time_hours || 0}h`} />
                  <Field k="Down reason" v={d.down_time_reason || "—"} />
                  <Field k="Supplier" v={d.supplier || "—"} />
                  <Field k="SMU start / finish" v={d.machine_start != null && d.machine_finish != null ? `${d.machine_start} → ${d.machine_finish}` : "—"} />
                  <Field k="SMU used" v={smuUsed} />
                </div>
                <div className="border-t border-divider-inner px-4 py-3 flex gap-3">
                  <span className={cardLbl + " shrink-0 pt-0.5"}>Notes</span>
                  <span className="text-[14px] text-ink-secondary">{d.notes || "—"}</span>
                </div>
              </>
            ) : (
              <div className="grid grid-cols-3 gap-3 p-4">
                <div><label className={lbl}>Date</label><input type="date" className={inp} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="docket-edit-date" /></div>
                <div><label className={lbl}>Start</label><input type="time" className={inp} value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="docket-edit-start" /></div>
                <div><label className={lbl}>Finish</label><input type="time" className={inp} value={form.finish_time} onChange={(e) => set("finish_time", e.target.value)} data-testid="docket-edit-finish" /></div>
                <div><label className={lbl}>Down time (h)</label><input type="number" step="0.25" className={inp} value={form.down_time_hours} onChange={(e) => set("down_time_hours", e.target.value)} data-testid="docket-edit-downtime" /></div>
                <div><label className={lbl}>SMU Start</label><input type="number" step="0.1" className={inp} value={form.machine_start ?? ""} onChange={(e) => set("machine_start", e.target.value)} data-testid="docket-edit-smu-start" /></div>
                <div><label className={lbl}>SMU Finish</label><input type="number" step="0.1" className={inp} value={form.machine_finish ?? ""} onChange={(e) => set("machine_finish", e.target.value)} data-testid="docket-edit-smu-finish" /></div>
                <div className="col-span-2"><label className={lbl}>Down reason</label><input className={inp} value={form.down_time_reason || ""} onChange={(e) => set("down_time_reason", e.target.value)} /></div>
                <div className="col-span-3"><label className={lbl}>Notes</label><textarea className={inp + " h-16 py-1"} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
                <div className="col-span-3 bg-surface-card border border-border rounded-[10px] p-3 space-y-2" data-testid="docket-edit-tasks">
                  <div className="flex items-center justify-between">
                    <label className={lbl + " mb-0"}>Tasks / Activities</label>
                    <button onClick={() => set("task_entries", [...(form.task_entries || []), { task_id: null, code: "", name: "", hours: "", manual: true }])}
                      data-testid="docket-edit-task-add" className="text-[12px] font-bold text-accent-ink hover:underline">+ Add task</button>
                  </div>
                  {(form.task_entries || []).map((t, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`docket-edit-task-row-${i}`}>
                      <select className="flex-1 min-w-0 h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" value={t.task_id || "__manual__"} data-testid={`docket-edit-task-select-${i}`}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "__manual__") setTaskEntry(i, { task_id: null, manual: true });
                          else { const pt = projTasks.find((x) => x.id === v) || {}; setTaskEntry(i, { task_id: v, code: pt.code || "", name: pt.name || "", manual: false }); }
                        }}>
                        {projTasks.map((pt) => <option key={pt.id} value={pt.id}>{pt.code ? `${pt.code} — ` : ""}{pt.name}</option>)}
                        <option value="__manual__">Manual entry…</option>
                      </select>
                      {!t.task_id && (
                        <input className="flex-1 min-w-0 h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" placeholder="Task name" value={t.name || ""} data-testid={`docket-edit-task-name-${i}`}
                          onChange={(e) => setTaskEntry(i, { name: e.target.value })} />
                      )}
                      <input type="number" step="0.25" className="w-20 shrink-0 h-9 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" placeholder="Hrs" value={t.hours ?? ""} data-testid={`docket-edit-task-hours-${i}`}
                        onChange={(e) => setTaskEntry(i, { hours: e.target.value })} />
                      <button onClick={() => set("task_entries", form.task_entries.filter((_, j) => j !== i))} data-testid={`docket-edit-task-remove-${i}`}
                        className="text-ink-tertiary hover:text-red-600 px-1">×</button>
                    </div>
                  ))}
                  {(form.task_entries || []).length === 0 && <p className="text-[11px] text-ink-tertiary">No tasks recorded.</p>}
                </div>
                <div className="col-span-3 flex gap-2">
                  <button onClick={saveAmend} disabled={busy} data-testid="docket-edit-save" className="h-9 px-4 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.04em] font-bold rounded-[7px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1"><FloppyDisk size={14} /> Save amendments</button>
                  <button onClick={() => { setEditing(false); setForm(d); }} className="h-9 px-4 border border-border-control text-sm rounded-[7px] hover:bg-surface-panel">Cancel</button>
                </div>
              </div>
            )}
          </Card>

          {/* Tasks / activities (view) */}
          {!editing && (d.task_entries || []).length > 0 && (
            <Card title="Tasks / activities" testid="docket-detail-tasks">
              <table className="w-full text-[13.5px]">
                <thead>
                  <tr className="border-b border-divider-inner">
                    <th className="text-left px-4 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-[11px]">Code</th>
                    <th className="text-left px-4 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-[11px]">Task</th>
                    <th className="text-right px-4 py-2.5 font-medium text-ink-muted uppercase tracking-[0.1em] text-[11px]">Hours</th>
                  </tr>
                </thead>
                <tbody>
                  {d.task_entries.map((t, i) => (
                    <tr key={i} className="border-b border-divider-row" data-testid={`docket-detail-task-${i}`}>
                      <td className="px-4 py-2 font-mono text-ink-secondary">{t.code || (t.manual ? "manual" : "—")}</td>
                      <td className="px-4 py-2 text-ink-primary">{t.name}</td>
                      <td className="px-4 py-2 text-right font-semibold text-ink-primary">{t.hours ?? "—"}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-4 py-2.5 font-bold text-ink-primary" colSpan={2}>Total</td>
                    <td className="px-4 py-2.5 text-right font-bold text-ink-primary" data-testid="docket-detail-task-total">{taskTotal}</td>
                  </tr>
                </tbody>
              </table>
            </Card>
          )}

          {/* Signatures */}
          <div className="grid grid-cols-2 gap-3">
            <SigCard role="operator" sig={d.signatures?.operator} onCapture={capture} />
            <SigCard role="site_rep" sig={d.signatures?.site_rep} onCapture={capture} />
          </div>

          {/* Trail */}
          <div className="bg-surface-card border border-border rounded-[10px] px-4 py-3 flex gap-3" data-testid="docket-approvals">
            <span className={cardLbl + " shrink-0 pt-0.5"}>Trail</span>
            <span className="text-[13.5px] text-ink-secondary">{trailText}</span>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-t border-border bg-surface-panel shrink-0">
          <p className="text-[12.5px] text-ink-tertiary max-w-[56%] leading-snug">
            {d.status === "rejected" ? "This docket was rejected and returned to the worker."
              : d.status === "office_approved" ? "This docket is fully approved and passed to the office."
              : footerHint}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            {isWriter && nxt && (
              <>
                <button onClick={() => act("reject")} disabled={busy} data-testid="docket-detail-reject"
                  className="text-[13px] font-bold text-red-600 hover:underline disabled:opacity-50">Reject</button>
                <button onClick={() => act("approve")} disabled={busy} data-testid="docket-detail-approve"
                  className="h-11 px-6 bg-equipr-yellow text-equipr-black text-[12px] uppercase tracking-[0.05em] font-bold rounded-[10px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px inline-flex items-center gap-1.5">{nxt}</button>
              </>
            )}
            {d.status === "office_approved" && <span className="text-[13px] font-semibold text-green-700 inline-flex items-center gap-1"><CheckCircle size={16} weight="fill" /> Fully approved</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
